/**
 * Date formatting utilities for the web app
 */

/**
 * Today's date in LOCAL time as 'YYYY-MM-DD' — matches the DatePicker's local-time basis
 * (its "Today" button and highlight both use `new Date()` in local time).
 */
export function todayISO(): string {
  const d = new Date();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mo}-${da}`;
}

/**
 * Format a date to a human-readable locale string: "Jan 1, 2024"
 * Accepts ISO strings, Date objects, or null/undefined (returns "—")
 */
export function formatLocalDate(d: string | Date | undefined | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a date+time to a human-readable locale string: "Jan 1, 2024, 02:30 PM"
 * Accepts ISO strings, Date objects, or null/undefined (returns "—")
 */
export function formatLocalDateTime(d: string | Date | undefined | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Return a relative-time label: "just now", "5m ago", "3h ago", "2d ago"
 */
export function formatRelativeTime(dateString: string): string {
  const diffMins = Math.floor((Date.now() - new Date(dateString).getTime()) / 60_000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

/**
 * Return an age label in days: "Today", "1 day", "5 days"
 */
export function formatAgeInDays(dateString: string): string {
  const diffDays = Math.floor((Date.now() - new Date(dateString).getTime()) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1 day';
  return `${diffDays} days`;
}

/**
 * Convert a date value to a date input value (YYYY-MM-DD)
 * Returns "" for null/undefined
 */
export function toDateInputValue(d: string | Date | null | undefined): string {
  if (!d) return '';
  return new Date(d).toISOString().split('T')[0];
}

/**
 * Format a 'YYYY-MM-DD' string for human display, e.g. "June 28, 2026".
 * Uses local-date parsing (splits on '-') to avoid timezone offset shifting
 * the day when the ISO string is parsed as midnight UTC.
 * Returns the original string if it doesn't match YYYY-MM-DD.
 */
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

export function formatDisplayDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${MONTH_NAMES[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}
